<template>
  <v-scale-screen width="1920" height="1080" :fullScreen="true">
    <div class="ai-v2-screen v2-cockpit-theme">
      <header class="ai-screen-header">
        <ul class="ai-header-nav">
          <li @click="$router.push('/home')">
            <img src="../../assets/images/electricity/home-page.png" alt="" />
            <span>首页</span>
          </li>
          <li>
            <img src="../../assets/images/electricity/date.png" alt="" />
            <span>{{ nowText }}</span>
          </li>
        </ul>
        <div class="ai-screen-title">
          <img src="../../assets/images/electricity/logo.png" alt="" />
          <span>AI 能源策略中心</span>
        </div>
        <ul class="ai-header-operate">
          <li><span class="demo-label">DEMO DATA · 展示层</span></li>
          <li><img src="../../assets/images/cockpit/address.png" alt="" /><span>京津冀示范场站</span></li>
        </ul>
      </header>

      <main class="ai-screen-main">
        <aside class="ai-left-column">
          <section class="ai-panel ai-forecast-summary">
            <header><i></i><span>未来 24h 预测</span><em>FORECAST</em></header>
            <ul>
              <li v-for="item in forecasts" :key="item.name" :class="item.type">
                <span>{{ item.name }}</span>
                <strong>{{ item.value }}</strong>
                <em>{{ item.note }}</em>
                <div><i :style="{ width: `${item.confidence}%` }"></i></div>
                <small>置信度 {{ item.confidence }}%</small>
              </li>
            </ul>
          </section>
          <section class="ai-panel ai-model-status">
            <header><i></i><span>展示模型状态</span><em>MODEL STATUS</em></header>
            <ul>
              <li><span>发电预测</span><strong>展示数据</strong></li>
              <li><span>负荷预测</span><strong>展示数据</strong></li>
              <li><span>电价预测</span><strong>展示数据</strong></li>
              <li><span>策略引擎</span><strong class="waiting">待接入</strong></li>
            </ul>
          </section>
        </aside>

        <section class="ai-center-column">
          <div class="ai-mode-switch" role="tablist" aria-label="AI 时间范围">
            <button type="button" :class="{ active: range === '24h' }" @click="range = '24h'">未来 24h</button>
            <button type="button" :class="{ active: range === '7d' }" @click="range = '7d'">未来 7 天</button>
            <button type="button" :class="{ active: range === 'backtest' }" @click="range = 'backtest'">历史回测</button>
          </div>
          <section class="ai-twin-stage">
            <div class="ai-stage-heading">
              <span>未来 24h 联合预测</span>
              <strong>发电 / 负荷 / 电价</strong>
              <small>预测展示不会下发控制指令</small>
            </div>
            <StorageEnergyScene :station="storageStation" mode="forecast" :labels-visible="false" :flows-visible="true" />
          </section>
          <section class="ai-curve-panel ai-panel">
            <header><i></i><span>未来 24h 发电 / 负荷 / 电价预测</span><em>DEMO CURVES</em></header>
            <div ref="forecastChart" class="ai-forecast-chart"></div>
          </section>
        </section>

        <aside class="ai-right-column">
          <section class="ai-panel ai-insight-panel">
            <header><i></i><span>AI Insight</span><em>DEMO SUMMARY</em></header>
            <div class="insight-score"><span>策略可信度</span><strong>86</strong><em>/100</em></div>
            <h3>晚高峰前预充，峰时有序放电</h3>
            <p>预计 19:00 总负荷达到 2.88MW。建议储能于 15:30—17:40 以 220kW 充电，18:30 后转入放电准备。</p>
            <ul>
              <li><span>预计削峰</span><strong>420 kW</strong></li>
              <li><span>预计绿电消纳</span><strong>+6.8%</strong></li>
              <li><span>风险等级</span><strong class="low">低</strong></li>
            </ul>
          </section>
          <section class="ai-panel ai-strategy-panel">
            <header><i></i><span>储能策略建议</span><em>ADVISORY ONLY</em></header>
            <ol>
              <li v-for="(item, index) in strategy" :key="item.time">
                <b>{{ String(index + 1).padStart(2, '0') }}</b>
                <span><strong>{{ item.time }}</strong><em>{{ item.action }}</em></span>
                <small>{{ item.power }}</small>
              </li>
            </ol>
            <div class="advisory-note">仅供展示与人工研判 · 不自动执行</div>
          </section>
        </aside>
      </main>
    </div>
  </v-scale-screen>
</template>

<script>
import * as echarts from "echarts";
import StorageEnergyScene from "../../modules/energy-demo/scenes/StorageEnergyScene.vue";
import { getStations } from "../../modules/energy-demo/data/demoData";
import { registerV2EchartsTheme } from "../../modules/v2-cockpit/theme/echartsTheme";

const cockpitTheme = registerV2EchartsTheme(echarts);

export default {
  name: "AiCenterV2",
  components: { StorageEnergyScene },
  data() {
    return {
      nowText: "",
      range: "24h",
      timer: null,
      chart: null,
      forecasts: [
        { name: "明日发电预测", value: "6.82 MWh", note: "较今日 +9.1%", confidence: 88, type: "source" },
        { name: "明日负荷预测", value: "41.6 MWh", note: "峰值 2.88MW", confidence: 91, type: "load" },
        { name: "明日电价预测", value: "0.74 元/kWh", note: "峰段 18:00—21:00", confidence: 82, type: "price" },
      ],
      strategy: [
        { time: "10:30—14:20", action: "消纳光伏余电", power: "+180kW" },
        { time: "15:30—17:40", action: "低价预充", power: "+220kW" },
        { time: "18:30—21:10", action: "晚峰放电", power: "-420kW" },
      ],
      storageStation: getStations("storage")[0],
    };
  },
  watch: {
    range() { this.$nextTick(this.drawForecastChart); },
  },
  mounted() {
    this.updateNow();
    this.timer = setInterval(this.updateNow, 1000);
    this.$nextTick(this.drawForecastChart);
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    window.removeEventListener("resize", this.resizeChart);
    this.chart && this.chart.dispose();
  },
  methods: {
    updateNow() {
      const now = new Date();
      const pad = (value) => String(value).padStart(2, "0");
      const week = ["日", "一", "二", "三", "四", "五", "六"][now.getDay()];
      this.nowText = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} 星期${week} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    },
    resizeChart() {
      this.chart && this.chart.resize();
    },
    drawForecastChart() {
      if (!this.chart) this.chart = echarts.init(this.$refs.forecastChart, cockpitTheme);
      this.chart.clear();
      const profiles = {
        "24h": { times: ["00", "03", "06", "09", "12", "15", "18", "21", "24"], source: [0, 0, .12, .62, 1.18, 1.26, .48, .02, 0], load: [.62, .54, .86, 1.62, 2.08, 2.36, 2.88, 1.68, .92], price: [.31, .28, .34, .52, .58, .62, .98, .86, .46] },
        "7d": { times: ["周一", "周二", "周三", "周四", "周五", "周六", "周日"], source: [1.02, 1.18, .92, 1.26, 1.14, 1.08, 1.22], load: [2.42, 2.68, 2.51, 2.82, 2.88, 2.36, 2.18], price: [.68, .72, .64, .78, .84, .59, .55] },
        backtest: { times: ["-7d", "-6d", "-5d", "-4d", "-3d", "-2d", "昨日"], source: [.96, 1.08, 1.2, 1.12, .82, 1.18, 1.04], load: [2.31, 2.56, 2.74, 2.62, 2.88, 2.7, 2.52], price: [.61, .68, .74, .7, .82, .76, .66] },
      };
      const profile = profiles[this.range] || profiles["24h"];
      this.chart.setOption({
        animationDuration: 900,
        tooltip: { trigger: "axis" },
        legend: { right: 16, top: 8, data: ["发电预测", "负荷预测", "电价预测"] },
        grid: { left: 45, right: 42, top: 44, bottom: 28 },
        xAxis: { type: "category", data: profile.times, boundaryGap: false },
        yAxis: [
          { type: "value", name: "MW", min: 0, max: 3.2 },
          { type: "value", name: "元/kWh", min: 0.2, max: 1.2 },
        ],
        series: [
          { name: "发电预测", type: "line", smooth: true, symbol: "none", data: profile.source, lineStyle: { width: 2 }, areaStyle: { opacity: .08 } },
          { name: "负荷预测", type: "line", smooth: true, symbol: "none", data: profile.load, lineStyle: { width: 2 } },
          { name: "电价预测", type: "line", smooth: true, symbol: "none", yAxisIndex: 1, data: profile.price, lineStyle: { width: 2, type: "dashed" } },
        ],
      });
    },
  },
};
</script>

<style>
@import "../../modules/v2-cockpit/theme/tokens.css";

.ai-v2-screen { width: 1920px; height: 1080px; overflow: hidden; color: #91bce0; background: url("../../assets/images/electricity/background.jpg") center/cover no-repeat; }
.ai-screen-header { height: 92px; padding: 15px 22px 0 29px; box-sizing: border-box; display: flex; justify-content: space-between; align-items: flex-start; background: url("../../assets/images/electricity/header-background.png") center/cover no-repeat; }
.ai-header-nav,
.ai-header-operate { width: 560px; height: 42px; margin: 0; padding: 0; display: flex; align-items: center; list-style: none; }
.ai-header-operate { justify-content: flex-end; }
.ai-header-nav li,
.ai-header-operate li { display: flex; align-items: center; margin-right: 25px; color: #dcecff; font-size: 14px; }
.ai-header-nav li:first-child { cursor: pointer; }
.ai-header-nav img,
.ai-header-operate img { width: 18px; margin-right: 9px; }
.ai-screen-title { display: flex; align-items: center; height: 42px; }
.ai-screen-title img { width: 34px; margin-right: 11px; }
.ai-screen-title span { color: #e9f7ff; font-size: 28px; font-weight: 700; }
.demo-label { padding: 6px 9px; border: 1px solid rgba(244, 213, 72, .42); color: #f5d94c; background: rgba(63, 48, 4, .45); font-size: 11px; letter-spacing: .6px; }
.ai-screen-main { height: 988px; padding: 18px 26px 28px; box-sizing: border-box; display: grid; grid-template-columns: 348px 1fr 388px; gap: 20px; }
.ai-left-column,
.ai-center-column,
.ai-right-column { min-width: 0; }
.ai-center-column { position: relative; }
.ai-panel { position: relative; box-sizing: border-box; padding: 50px 18px 16px; background: linear-gradient(145deg, rgba(5, 28, 72, .86), rgba(2, 13, 42, .78)); border: 1px solid rgba(48, 157, 228, .32); box-shadow: inset 0 0 32px rgba(28, 106, 200, .12); }
.ai-panel > header { position: absolute; left: 16px; right: 16px; top: 14px; display: flex; align-items: center; height: 22px; border-bottom: 1px solid rgba(57, 137, 211, .2); }
.ai-panel > header > i { width: 8px; height: 8px; margin-right: 8px; border: 1px solid #34e7d7; transform: rotate(45deg); }
.ai-panel > header span { color: #e7f6ff; font-size: 15px; font-weight: 700; }
.ai-panel > header em { margin-left: auto; color: #4e7da6; font-size: 9px; font-style: normal; }
.ai-forecast-summary { height: 590px; }
.ai-forecast-summary ul,
.ai-model-status ul { margin: 0; padding: 0; list-style: none; }
.ai-forecast-summary li { height: 156px; margin-bottom: 16px; padding: 18px 15px; box-sizing: border-box; background: rgba(10, 48, 101, .46); border-left: 3px solid #35efda; }
.ai-forecast-summary li.load { border-left-color: #f4d94d; }
.ai-forecast-summary li.price { border-left-color: #ff9258; }
.ai-forecast-summary span,
.ai-forecast-summary strong,
.ai-forecast-summary em { display: block; }
.ai-forecast-summary span { color: #89afd1; font-size: 12px; }
.ai-forecast-summary strong { margin-top: 9px; color: #eafaff; font-size: 28px; font-family: Consolas, monospace; }
.ai-forecast-summary em { margin-top: 6px; color: #4fe7d7; font-size: 11px; font-style: normal; }
.ai-forecast-summary li > div { height: 5px; margin-top: 13px; background: rgba(35, 81, 132, .64); }
.ai-forecast-summary li > div i { display: block; height: 100%; background: linear-gradient(90deg, #2e8fdc, #36efd8); }
.ai-forecast-summary small { display: block; margin-top: 5px; color: #628cb1; font-size: 9px; text-align: right; }
.ai-model-status { height: 338px; margin-top: 18px; }
.ai-model-status li { height: 58px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(57, 130, 203, .18); font-size: 12px; }
.ai-model-status strong { color: #3be9d6; font-size: 12px; }
.ai-model-status strong.waiting { color: #f4d94d; }
.ai-mode-switch { position: absolute; z-index: 10; top: 0; left: 50%; transform: translateX(-50%); display: flex; gap: 6px; }
.ai-mode-switch button { width: 116px; height: 34px; border: 1px solid rgba(50, 141, 218, .28); background: rgba(4, 25, 66, .7); color: #77a3ca; cursor: pointer; }
.ai-mode-switch button.active { color: #f1ffff; border-color: #34dfd5; background: linear-gradient(90deg, rgba(27, 120, 179, .66), rgba(31, 200, 190, .28)); }
.ai-twin-stage { position: relative; height: 558px; margin-top: 52px; border: 1px solid rgba(53, 147, 218, .2); overflow: hidden; }
.ai-twin-stage .storage-three-stage { position: absolute; inset: 0; }
.ai-twin-stage .storage-scene-title { display: none; }
.ai-stage-heading { position: absolute; z-index: 12; top: 16px; left: 20px; pointer-events: none; }
.ai-stage-heading span,
.ai-stage-heading strong,
.ai-stage-heading small { display: block; }
.ai-stage-heading span { color: #35e8da; font-size: 9px; letter-spacing: 1.5px; }
.ai-stage-heading strong { margin-top: 5px; color: #eaf7ff; font-size: 20px; }
.ai-stage-heading small { margin-top: 5px; color: #658eb3; font-size: 9px; }
.ai-twin-stage .scene-status { display: none; }
.ai-twin-stage .hud-card { transform: scale(.88); }
.ai-twin-stage .hud-wind,
.ai-twin-stage .hud-load { transform-origin: left center; }
.ai-twin-stage .hud-grid,
.ai-twin-stage .hud-storage { transform-origin: right center; }
.ai-curve-panel { height: 356px; margin-top: 18px; padding: 44px 0 0; }
.ai-forecast-chart { width: 100%; height: 306px; }
.ai-insight-panel { height: 510px; }
.insight-score { display: flex; align-items: baseline; height: 76px; padding: 0 14px; background: rgba(11, 54, 106, .44); }
.insight-score span { align-self: center; color: #82a7c8; font-size: 11px; }
.insight-score strong { margin-left: auto; color: #48eadb; font-size: 44px; font-family: Consolas, monospace; }
.insight-score em { color: #6f9abd; font-size: 12px; font-style: normal; }
.ai-insight-panel h3 { margin: 22px 0 0; color: #e9f7ff; font-size: 18px; }
.ai-insight-panel p { margin: 14px 0 0; color: #83a9cb; font-size: 12px; line-height: 1.9; }
.ai-insight-panel ul { margin: 18px 0 0; padding: 0; list-style: none; }
.ai-insight-panel li { height: 49px; display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(52, 126, 198, .18); }
.ai-insight-panel li span { font-size: 11px; }
.ai-insight-panel li strong { color: #4ce8d8; font-size: 16px; }
.ai-insight-panel li strong.low { color: #f4d94d; }
.ai-strategy-panel { height: 436px; margin-top: 18px; }
.ai-strategy-panel ol { margin: 0; padding: 0; list-style: none; }
.ai-strategy-panel li { height: 82px; display: grid; grid-template-columns: 34px 1fr 70px; align-items: center; border-bottom: 1px solid rgba(52, 126, 198, .18); }
.ai-strategy-panel li > b { color: #2b82c6; font-size: 24px; font-family: Consolas, monospace; }
.ai-strategy-panel li span strong,
.ai-strategy-panel li span em { display: block; }
.ai-strategy-panel li span strong { color: #dff4ff; font-size: 12px; }
.ai-strategy-panel li span em { margin-top: 7px; color: #769cc0; font-size: 10px; font-style: normal; }
.ai-strategy-panel li > small { color: #43e7d5; font-size: 13px; text-align: right; }
.advisory-note { position: absolute; left: 18px; right: 18px; bottom: 16px; height: 34px; display: grid; place-items: center; color: #f4d94d; background: rgba(64, 48, 4, .28); border: 1px solid rgba(244, 217, 77, .26); font-size: 10px; }
</style>
